/**
 * Single source of truth for page visibility defaults.
 * Used by userTeamService, adminService, server, and Edit* components.
 */

const PAGE_IDS = {
  /** @deprecated Retired GA Portal tab — migrated to advisoryPortal or sendEnvelope. */
  CLIENTS: 'clients',
  CLIENT_OVERVIEW: 'clientOverview',
  ADVICE_DATES: 'adviceDates',
  FACT_FIND: 'factFind',
  INSURANCE_NEEDS: 'insuranceNeeds',
  ADVICE: 'advice',

  FILE_NOTE: 'fileNote',
  AMENDMENTS: 'amendments',
  AR_WIZARD: 'arWizard',
  DOCUMENTS: 'documents',
  MESSAGES: 'messages',
  ADVISORY_PORTAL: 'advisoryPortal',
  PARAPLANNER_PORTAL: 'paraplannerPortal',
  CALENDAR: 'calendar',
  /** Outlook email workspace (Sept 2026). Not in any default role list — Page Visibility is the rollout switch. */
  EMAIL: 'email',
  /**
   * Guided Advice (Oct 2026): chat-style alternative to Recommendations. Not in any default
   * role list — individual advisers are granted it in Page Visibility → Per person.
   */
  GUIDED_ADVICE: 'guidedAdvice',
  /** @deprecated use granular ADMIN_* ids */
  ADMIN: 'admin',
  ADMIN_MANUAL_OVERRIDE: 'adminManualOverride',
  ADMIN_PARAPLANNER_OVERRIDE: 'adminParaplannerOverride',
  ADMIN_PAGE_VISIBILITY: 'adminPageVisibility',
  ADMIN_DEFAULT_FILTERS: 'adminDefaultFilters',
  ADMIN_TEAMS: 'adminTeams',
  ADMIN_SMS_NUMBERS: 'adminSmsNumbers',
  ADMIN_ID_UPLOAD_SMS: 'adminIdUploadSms',
  ADMIN_SMS_SCHEDULING: 'adminSmsScheduling',
  SMS_BROADCAST: 'smsBroadcast',
  ADMIN_DOCUSIGN_SETTINGS: 'adminDocusignSettings',
  ADMIN_FORM_CONFIG: 'adminFormConfig',
  ADMIN_PORTFOLIOS: 'adminPortfolios',
  ADMIN_AI_PROMPTS: 'adminAiPrompts',
  ADMIN_SYSTEM_EMAILS: 'adminSystemEmails',
  ADMIN_CORRESPONDENCE: 'adminCorrespondence',
  ADMIN_BANNER: 'adminBanner',
  ADMIN_MARKETING_IMPORTS: 'adminMarketingImports',
  ADMIN_CLIENT_JSON: 'adminClientJson',
  ADMIN_DOCX_TEMPLATES: 'adminDocxTemplates',
  /** @deprecated Layout tab retired Oct 2026 — an alias of Admin → Documents (adminPages.js). */
  ADMIN_DOCUMENT_LAYOUT: 'adminDocumentLayout',
  ADMIN_DEPLOYMENTS: 'adminDeployments',
  ADMIN_DOCUMENT_QA: 'adminDocumentQa',
  ADMIN_AR_DOCUMENT_TEST: 'adminArDocumentTest',
  /** @deprecated In-app document template editor retired Oct 2026 (PDF templates live in code) — redirects to Admin → Documents. */
  ADMIN_DOCUMENT_CANVAS: 'adminDocumentCanvas',
  // Retired Sept 2026 (merged into Advice Dates); the id redirects there.
  ADMIN_FEE_DISCLOSURE: 'adminFeeDisclosure',
  ADMIN_WORKFLOWS: 'adminWorkflows',
  ADMIN_ASSUMPTIONS: 'adminAssumptions',
  /** Settings → Client Allocation (Oct 2026): Off / Manual / Automatic onboarding allocation per firm. */
  ADMIN_CLIENT_ALLOCATION: 'adminClientAllocation',
  /** Settings → Guided Advice Sandbox (Oct 2026): background drafts on anonymised test clients. */
  ADMIN_GUIDED_SANDBOX: 'adminGuidedSandbox',
  /** Settings → Settings in code (Oct 2026): read-only view of the settings kept in the repo. */
  ADMIN_SETTINGS_IN_CODE: 'adminSettingsInCode',
  ADMIN_CHART_SETTINGS: 'adminChartSettings',
  ADMIN_DOCUMENT_STYLING: 'adminDocumentStyling',
  /** @deprecated Shared-wording editor retired Oct 2026 (wording lives in code) — an alias of Admin → Documents. */
  ADMIN_CONTENT_BLOCKS: 'adminContentBlocks',
  ADMIN_DOCUMENTS: 'adminDocuments',
  AUDIT_LOG: 'auditLog',
  E_SIGNATURES: 'eSignatures',
  SEND_ENVELOPE: 'sendEnvelope',
  REVERSE_FACT_FIND: 'reverseFactFind',
  /**
   * @deprecated Client Meeting workspace (Sept 2026), retired Oct 2026 — replaced by
   * advice scenarios on Recommendations / Amendments; old links go home. Page ids
   * are permanent: never reuse this one.
   */
  CLIENT_MEETING: 'clientMeeting',
  /** @deprecated Retired Sept 2026 (into Client Meeting, itself retired Oct 2026) — redirects home. */
  QUICK_QUOTE: 'quickQuote',
  /** @deprecated Retired Sept 2026 (into Client Meeting, itself retired Oct 2026) — redirects home. */
  PROJECTIONS: 'projections',
  /** @deprecated Retired Sept 2026 — the Methodology sheet now opens from Recommendations; the id redirects home. */
  METHODOLOGY: 'methodology',
  ADVICE_JOURNEY: 'adviceJourney',
  CSV_MERGE: 'csvMerge',
  GUIDES: 'guides',
  FAQS: 'faqs',
  HELP_DESK: 'helpDesk',
  // Retired Oct 2026 (the Oversight pages): ids kept so stored Page Visibility
  // lists and old links resolve; navConfig RETIRED_TAB_REDIRECTS sends them to Reports.
  REPORTING_WORKLOAD: 'reportingWorkload',
  REPORTING_THROUGHPUT: 'reportingThroughput',
  REPORTING_REWORK: 'reportingRework',
  REPORTING_ONBOARDING_FUNNEL: 'reportingOnboardingFunnel',
  REPORTING_COMMUNICATIONS: 'reportingCommunications',
  // Reports (Oct 2026, replaced the Oversight pages). Nav order = REPORTS_PAGE_IDS order.
  REPORTS_MARKETING: 'reportsMarketing',
  REPORTS_PARAPLANNING: 'reportsParaplanning',
  REPORTS_ADVICE_APPROVALS: 'reportsAdviceApprovals',
  REPORTS_IMPLEMENTATION: 'reportsImplementation',
  REPORTS_SIGNED_DOCUMENTS: 'reportsSignedDocuments',
  REPORTS_FSG_EMAILS: 'reportsFsgEmails',
  REPORTS_UNCONVERTED: 'reportsUnconverted',
  /** @deprecated Retired Sept 2026 (into Client Meeting, itself retired Oct 2026) — redirects home. */
  AUTO_NOTE_BETA: 'autoNoteBeta',
  MESSAGES_INBOX: 'messagesInbox',
  FILE_NOTES: 'fileNotes',
  COMPLIANCE_DASHBOARD: 'complianceDashboard',
};

const REPORTS_PAGE_IDS = [
  PAGE_IDS.REPORTS_MARKETING,
  PAGE_IDS.REPORTS_PARAPLANNING,
  PAGE_IDS.REPORTS_ADVICE_APPROVALS,
  PAGE_IDS.REPORTS_IMPLEMENTATION,
  PAGE_IDS.REPORTS_SIGNED_DOCUMENTS,
  PAGE_IDS.REPORTS_FSG_EMAILS,
  PAGE_IDS.REPORTS_UNCONVERTED,
];

const ALL_ADMIN_PAGE_IDS = [
  PAGE_IDS.ADMIN_MANUAL_OVERRIDE,
  PAGE_IDS.ADMIN_PARAPLANNER_OVERRIDE,
  PAGE_IDS.ADMIN_PAGE_VISIBILITY,
  PAGE_IDS.ADMIN_DEFAULT_FILTERS,
  PAGE_IDS.ADMIN_TEAMS,
  PAGE_IDS.ADMIN_SMS_NUMBERS,
  PAGE_IDS.ADMIN_ID_UPLOAD_SMS,
  PAGE_IDS.ADMIN_SMS_SCHEDULING,
  PAGE_IDS.ADMIN_DOCUSIGN_SETTINGS,
  PAGE_IDS.ADMIN_FORM_CONFIG,
  PAGE_IDS.ADMIN_PORTFOLIOS,
  PAGE_IDS.ADMIN_AI_PROMPTS,
  PAGE_IDS.ADMIN_SYSTEM_EMAILS,
  PAGE_IDS.ADMIN_CORRESPONDENCE,
  PAGE_IDS.ADMIN_BANNER,
  PAGE_IDS.ADMIN_MARKETING_IMPORTS,
  PAGE_IDS.ADMIN_CLIENT_JSON,
  PAGE_IDS.ADMIN_DOCUMENTS,
  PAGE_IDS.ADMIN_DEPLOYMENTS,
  PAGE_IDS.ADMIN_DOCUMENT_QA,
  PAGE_IDS.ADMIN_AR_DOCUMENT_TEST,
  PAGE_IDS.ADMIN_WORKFLOWS,
  PAGE_IDS.ADMIN_ASSUMPTIONS,
  PAGE_IDS.ADMIN_CLIENT_ALLOCATION,
  PAGE_IDS.ADMIN_GUIDED_SANDBOX,
  PAGE_IDS.ADMIN_SETTINGS_IN_CODE,
];

const basePages = [
  PAGE_IDS.CLIENT_OVERVIEW,
  PAGE_IDS.ADVICE_DATES,
  PAGE_IDS.FACT_FIND,
  PAGE_IDS.INSURANCE_NEEDS,
  PAGE_IDS.ADVICE,
  PAGE_IDS.FILE_NOTE,
  PAGE_IDS.AMENDMENTS,
  PAGE_IDS.ADVICE_JOURNEY,
  PAGE_IDS.REVERSE_FACT_FIND,
  PAGE_IDS.DOCUMENTS,
  PAGE_IDS.E_SIGNATURES,
  PAGE_IDS.MESSAGES,
  PAGE_IDS.ADVISORY_PORTAL,
  PAGE_IDS.CALENDAR
];

/** Default visible pages per role (global settings when Firestore is empty) */
const DEFAULT_VISIBLE_PAGES = {
  marketing: [PAGE_IDS.SEND_ENVELOPE, PAGE_IDS.E_SIGNATURES, PAGE_IDS.GUIDES],
  advisory: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.ADVICE_DATES, PAGE_IDS.FACT_FIND, PAGE_IDS.REVERSE_FACT_FIND, PAGE_IDS.ADVICE_JOURNEY, PAGE_IDS.MESSAGES, PAGE_IDS.ADVISORY_PORTAL, PAGE_IDS.CALENDAR, PAGE_IDS.GUIDES],
  superAdmin: [
    ...basePages,
    ...ALL_ADMIN_PAGE_IDS,
    PAGE_IDS.PARAPLANNER_PORTAL,
    PAGE_IDS.CSV_MERGE,
    PAGE_IDS.AUDIT_LOG,
    PAGE_IDS.GUIDES,
    PAGE_IDS.GUIDES,
  PAGE_IDS.FAQS,
    PAGE_IDS.HELP_DESK,
    ...REPORTS_PAGE_IDS,
    PAGE_IDS.COMPLIANCE_DASHBOARD,
  ],
  ufSupport: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.FACT_FIND, PAGE_IDS.ADVICE_JOURNEY, PAGE_IDS.MESSAGES, PAGE_IDS.ADVISORY_PORTAL, PAGE_IDS.GUIDES],
  arSupport: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.FACT_FIND, PAGE_IDS.ADVICE_JOURNEY, PAGE_IDS.MESSAGES, PAGE_IDS.ADVISORY_PORTAL, PAGE_IDS.GUIDES],
  arAdviser: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.ADVICE_DATES, PAGE_IDS.FACT_FIND, PAGE_IDS.ADVICE, PAGE_IDS.MESSAGES, PAGE_IDS.ADVISORY_PORTAL, PAGE_IDS.CALENDAR, PAGE_IDS.GUIDES],
  management: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.FACT_FIND, PAGE_IDS.REVERSE_FACT_FIND, PAGE_IDS.MESSAGES, PAGE_IDS.ADVISORY_PORTAL, PAGE_IDS.CALENDAR, PAGE_IDS.AUDIT_LOG, PAGE_IDS.COMPLIANCE_DASHBOARD, ...REPORTS_PAGE_IDS, PAGE_IDS.GUIDES],
  // complianceDashboard first: it doubles as the role's default landing tab
  compliance: [PAGE_IDS.COMPLIANCE_DASHBOARD, PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.FILE_NOTE, PAGE_IDS.E_SIGNATURES, PAGE_IDS.DOCUMENTS, PAGE_IDS.ADVISORY_PORTAL, ...REPORTS_PAGE_IDS, PAGE_IDS.GUIDES],
  paraplanner: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.ADVICE_DATES, PAGE_IDS.PARAPLANNER_PORTAL, PAGE_IDS.ADVICE, PAGE_IDS.AMENDMENTS, PAGE_IDS.REVERSE_FACT_FIND, PAGE_IDS.GUIDES],
  paraplannerManagement: [PAGE_IDS.CLIENT_OVERVIEW, PAGE_IDS.ADVICE_DATES, PAGE_IDS.PARAPLANNER_PORTAL, PAGE_IDS.ADVICE, PAGE_IDS.AMENDMENTS, PAGE_IDS.REVERSE_FACT_FIND, PAGE_IDS.ADMIN_PARAPLANNER_OVERRIDE, PAGE_IDS.GUIDES],
};

/** All page IDs for super-admin (for form conversion) */
const SUPER_ADMIN_PAGE_IDS = [
  ...basePages,
  ...ALL_ADMIN_PAGE_IDS,
  PAGE_IDS.PARAPLANNER_PORTAL,
  PAGE_IDS.CSV_MERGE,
  PAGE_IDS.AUDIT_LOG,
  PAGE_IDS.GUIDES,
  PAGE_IDS.FAQS,
  PAGE_IDS.HELP_DESK,
  PAGE_IDS.COMPLIANCE_DASHBOARD,
  ...REPORTS_PAGE_IDS,
];

/** All page IDs for paraplanner (for form conversion) */
const PARAPLANNER_PAGE_IDS = [
  ...basePages,
  PAGE_IDS.PARAPLANNER_PORTAL,
  PAGE_IDS.AUDIT_LOG,
  PAGE_IDS.ADMIN_PARAPLANNER_OVERRIDE,
  PAGE_IDS.GUIDES,
];

/** Base page IDs (marketing, advisory without role-specific) */
const BASE_PAGE_IDS = [...basePages, PAGE_IDS.GUIDES];

/**
 * Pages that can also be granted to individual people, on top of their role
 * (Page Visibility → Per person). Allowlisted so a stray grant can never open
 * an admin page. Stored as app_settings/navigation.pageGrants = { pageId: [emails] }.
 */
const PER_PERSON_PAGE_IDS = [PAGE_IDS.GUIDED_ADVICE];
const PAGE_GRANTS_KEY = 'pageGrants';

/** Grants for every allowlisted page: lowercased, trimmed, no duplicates, always an array. */
function normalisePageGrants(raw) {
  const out = {};
  PER_PERSON_PAGE_IDS.forEach((pageId) => {
    const list = raw && Array.isArray(raw[pageId]) ? raw[pageId] : [];
    const seen = {};
    out[pageId] = [];
    list.forEach((email) => {
      const value = String(email || '').trim().toLowerCase();
      if (!value || seen[value]) return;
      seen[value] = true;
      out[pageId].push(value);
    });
  });
  return out;
}

/** The role's pages plus any page granted to this person. */
function applyPageGrants(pages, navData, email) {
  const list = Array.isArray(pages) ? pages.slice() : [];
  const who = String(email || '').trim().toLowerCase();
  if (!who) return list;
  const grants = normalisePageGrants(navData && navData[PAGE_GRANTS_KEY]);
  PER_PERSON_PAGE_IDS.forEach((pageId) => {
    if (grants[pageId].indexOf(who) !== -1 && list.indexOf(pageId) === -1) list.push(pageId);
  });
  return list;
}

module.exports = {
  PER_PERSON_PAGE_IDS,
  PAGE_GRANTS_KEY,
  normalisePageGrants,
  applyPageGrants,
  PAGE_IDS,
  REPORTS_PAGE_IDS,
  ALL_ADMIN_PAGE_IDS,
  DEFAULT_VISIBLE_PAGES,
  SUPER_ADMIN_PAGE_IDS,
  PARAPLANNER_PAGE_IDS,
  BASE_PAGE_IDS,
};
